import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { sentenceCase } from '../../../../utils/utils';
import Text from '../../../elements/text/Text';
import TextInput from '../../../elements/text-input/TextInput';
import Modal from '../../../sections/modal/Modal';
import ModalButtons from '../../../sections/modal/ModalButtons';

const NewFolderButton = ({ action, loading }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const formSchema = object({
    name: string()
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('folderName', { ns: 'fields' }),
          })
        )
      )
      .matches(
        /^([a-zA-Z0-9][^:?]*)$/,
        sentenceCase(
          t('invalid', {
            ns: 'validations',
            field: t('folderName', { ns: 'fields' }),
          })
        )
      )
      .max(255, t('maxLength', { ns: 'validations', max: '255' })),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
    getValues,
  } = useForm({
    mode: 'onChange',
    defaultValue: '',
    resolver: yupResolver(formSchema),
  });

  const toggleModal = () => {
    reset();
    setOpen(!open);
  };

  const handleCreate = async () => {
    const folderName = getValues('name');
    toggleModal();
    await action(folderName);
  };

  return (
    <>
      <Button
        startIcon={<CreateNewFolderIcon />}
        onClick={toggleModal}
        size="small"
        variant="text"
        color="inherit"
        disabled={loading}
      >
        {t('buttons.newFolder', { ns: 'fileBrowser' })}
      </Button>
      <Modal
        action={
          <ModalButtons
            actionProps={{ disabled: !isDirty || !isValid }}
            actionText={t('save', { ns: 'buttons' })}
            onAction={handleCreate}
            onCancel={toggleModal}
          />
        }
        openModal={open}
        title={t('folderCreation', { ns: 'instance' })}
        handleClose={toggleModal}
      >
        <Text sx={{ mb: 4 }}>
          {t('descriptions.newFolder', { ns: 'fileBrowser' })}
        </Text>
        <form onSubmit={handleSubmit(handleCreate)} autoComplete="off">
          <TextInput
            name="name"
            label={t('folderName', { ns: 'fields' })}
            control={control}
            errors={errors}
            autoFocus
            fullWidth
            multiline={false}
          />
        </form>
      </Modal>
    </>
  );
};

NewFolderButton.propTypes = {
  action: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default NewFolderButton;
